// utils/multilingualUtils.js
// Utility functions for handling multilingual content in products and variants

/**
 * Get localized text based on current language
 * Supports both old format (string) and new format (object with language keys)
 * @param {string|object} textData - Text data (string or object with language keys)
 * @param {string} language - Current language code (e.g., 'en', 'ar')
 * @param {string} fallbackLanguage - Fallback language code (default: 'en')
 * @returns {string} Localized text
 */
export const getLocalizedText = (textData, language = 'en', fallbackLanguage = 'en') => {
    // Handle null/undefined
    if (!textData) return '';

    // If it's a string (old format), return as-is
    if (typeof textData === 'string') {
        return textData;
    }

    // If it's an object (new multilingual format)
    if (typeof textData === 'object' && textData !== null) {
        // Try current language first
        if (textData[language] && typeof textData[language] === 'string' && textData[language].trim()) {
            return textData[language];
        }

        // Try fallback language
        if (textData[fallbackLanguage] && typeof textData[fallbackLanguage] === 'string' && textData[fallbackLanguage].trim()) {
            return textData[fallbackLanguage];
        }

        // Try any available language
        const availableLanguages = Object.keys(textData);
        for (const lang of availableLanguages) {
            if (textData[lang] && typeof textData[lang] === 'string' && textData[lang].trim()) {
                return textData[lang];
            }
        }
    }

    // Return empty string if nothing found
    return '';
};

/**
 * Get localized product name
 * @param {object} product - Product object
 * @param {string} language - Current language code
 * @returns {string} Localized product name
 */
export const getLocalizedProductName = (product, language = 'en') => {
    if (!product) return '';
    return getLocalizedText(product.name, language);
};

/**
 * Get localized product description
 * @param {object} product - Product object
 * @param {string} language - Current language code
 * @returns {string} Localized product description
 */
export const getLocalizedProductDescription = (product, language = 'en') => {
    if (!product) return '';
    return getLocalizedText(product.description, language);
};

/**
 * Get localized option name
 * @param {object} option - Option object
 * @param {string} language - Current language code
 * @returns {string} Localized option name
 */
export const getLocalizedOptionName = (option, language = 'en') => {
    if (!option) return '';
    return getLocalizedText(option.name, language);
};

/**
 * Get localized option value (handles all values uniformly)
 * @param {string} value - Option value
 * @param {string} optionName - Option name (not used anymore, kept for compatibility)
 * @param {string} language - Current language code
 * @returns {string} Localized option value
 */
export const getLocalizedOptionValue = (value, optionName, language = 'en') => {
    if (!value) return '';

    // Handle all options uniformly - check if it's a multilingual object
    try {
        const parsedValue = JSON.parse(value);
        if (typeof parsedValue === 'object' && parsedValue !== null) {
            return getLocalizedText(parsedValue, language);
        }
    } catch {
        // Not JSON, return as-is
    }

    // Return plain value
    return getLocalizedText(value, language);
};

/**
 * Create localized display variant text
 * @param {object} product - Product data
 * @param {object} variant - Variant data
 * @param {string} language - Current language code
 * @returns {string} Localized display text for the variant
 */
export const createLocalizedDisplayVariantText = (product, variant, language = 'en') => {
    if (!variant) return '';

    // If the product has displayVariants, find the matching one
    if (product.displayVariants && Array.isArray(product.displayVariants)) {
        const displayVariant = product.displayVariants.find(dv => dv.variantId === variant.id);
        if (displayVariant && displayVariant.displayText) {
            return getLocalizedText(displayVariant.displayText, language);
        }
    }

    // Fallback: construct from variant properties with localization
    const variantParts = [];

    // Get options from product to know which properties to include
    if (product.options && Array.isArray(product.options)) {
        product.options.forEach(option => {
            const optionKey = option.name.toLowerCase();
            const value = variant[optionKey];

            if (value) {
                const localizedOptionName = getLocalizedOptionName(option, language);
                const localizedValue = getLocalizedOptionValue(value, option.name, language);

                if (localizedOptionName && localizedValue) {
                    variantParts.push(`${localizedOptionName}: ${localizedValue}`);
                }
            }
        });
    } else {
        // If no options, try common variant properties
        const commonProps = ['color', 'size', 'material', 'style', 'type'];
        commonProps.forEach(prop => {
            if (variant[prop]) {
                // Capitalize first letter for display
                const propName = prop.charAt(0).toUpperCase() + prop.slice(1);
                const localizedValue = getLocalizedOptionValue(variant[prop], prop, language);

                if (localizedValue) {
                    variantParts.push(`${propName}: ${localizedValue}`);
                }
            }
        });
    }

    return variantParts.join(' • ');
};

/**
 * Update product data structure to support multilingual content
 * This is a utility for migration purposes
 * @param {object} product - Product object
 * @param {string} currentLanguage - Current language of existing data
 * @returns {object} Updated product with multilingual structure
 */
export const migrateProductToMultilingual = (product, currentLanguage = 'en') => {
    if (!product) return product;

    const updatedProduct = { ...product };

    // Migrate name
    if (typeof product.name === 'string') {
        updatedProduct.name = {
            [currentLanguage]: product.name
        };
    }

    // Migrate description
    if (typeof product.description === 'string') {
        updatedProduct.description = {
            [currentLanguage]: product.description
        };
    }

    // Migrate options
    if (product.options && Array.isArray(product.options)) {
        updatedProduct.options = product.options.map(option => {
            const updatedOption = { ...option };

            // Migrate option name
            if (typeof option.name === 'string') {
                updatedOption.name = {
                    [currentLanguage]: option.name
                };
            }

            // Migrate option values uniformly
            if (option.values && Array.isArray(option.values)) {
                updatedOption.values = option.values.map(value => {
                    // Convert all values to multilingual if they're strings
                    try {
                        JSON.parse(value);
                        return value; // Already JSON
                    } catch {
                        // Convert string to multilingual object
                        return JSON.stringify({
                            [currentLanguage]: value
                        });
                    }
                });
            }

            return updatedOption;
        });
    }

    // Migrate displayVariants
    if (product.displayVariants && Array.isArray(product.displayVariants)) {
        updatedProduct.displayVariants = product.displayVariants.map(displayVariant => ({
            ...displayVariant,
            displayText: typeof displayVariant.displayText === 'string'
                ? { [currentLanguage]: displayVariant.displayText }
                : displayVariant.displayText
        }));
    }

    return updatedProduct;
};

/**
 * Create URL slug from multilingual text (always uses English for consistency)
 * @param {string|object} textData - Text data
 * @returns {string} URL-friendly slug
 */
export const createMultilingualSlug = (textData) => {
    // Always use English for URL generation to maintain consistency
    const englishText = getLocalizedText(textData, 'en', 'en');

    if (!englishText) {
        // If no English text, try any available language
        if (typeof textData === 'object' && textData !== null) {
            const availableLanguages = Object.keys(textData);
            for (const lang of availableLanguages) {
                if (textData[lang] && typeof textData[lang] === 'string' && textData[lang].trim()) {
                    return createSlugFromText(textData[lang]);
                }
            }
        }
        return '';
    }

    return createSlugFromText(englishText);
};

/**
 * Helper function to create slug from text
 * @param {string} text - Text to convert
 * @returns {string} URL slug
 */
const createSlugFromText = (text) => {
    if (!text) return '';

    return text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};
